import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Building2, MapPin, Calendar, Users, CreditCard, Lock,
  ChevronLeft, Check, Loader2, AlertCircle, Shield, Coffee
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../lib/api';
import PaymentBrandLogos from '../components/payment/PaymentBrandLogos';

const HotelBookingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  
  // Booking data from session
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [guestInfo, setGuestInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    nationality: 'TR'
  });
  
  const [cardInfo, setCardInfo] = useState({
    card_number: '',
    card_holder: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const expiryYearOptions = Array.from({ length: 12 }, (_, i) =>
    (new Date().getFullYear() + i).toString().slice(-2)
  );

  // Date locale
  const getDateLocale = () => {
    switch(i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  
  useEffect(() => {
    loadBookingData();
  }, []);

  useEffect(() => {
    const err = searchParams.get('error');
    if (!err) return;
    const msg = decodeURIComponent(err.replace(/\+/g, ' '));
    setPaymentError(msg);
    toast.error(msg, { duration: 12000 });
    const params = new URLSearchParams(searchParams);
    params.delete('error');
    const qs = params.toString();
    navigate({ pathname: window.location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [searchParams, navigate]);
  
  const loadBookingData = () => {
    try {
      const storedOffer = sessionStorage.getItem('selectedHotelOffer');
      if (!storedOffer) {
        toast.error('Rezervasyon bilgisi bulunamadı');
        navigate('/hotels');
        return;
      }
      
      const data = JSON.parse(storedOffer);
      setBookingData(data);
    } catch (error) {
      console.error('Error loading booking data:', error);
      toast.error('Bir hata oluştu');
      navigate('/hotels');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGuestChange = (field, value) => {
    setGuestInfo(prev => ({ ...prev, [field]: value }));
  };
  
  const handleCardChange = (field, value) => {
    if (field === 'card_number') {
      const digits = value.replace(/\D/g, '').slice(0, 16);
      value = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    }
    if (field === 'cvv') {
      value = value.replace(/\D/g, '').slice(0, 4);
    }
    setCardInfo(prev => ({ ...prev, [field]: value }));
  };

  const buildZiraatCardPayload = () => {
    const digits = cardInfo.card_number.replace(/\D/g, '');
    let year = cardInfo.expiry_year.replace(/\D/g, '');
    if (year.length === 4) year = year.slice(-2);
    return {
      card_number: digits,
      card_holder: cardInfo.card_holder.trim().toUpperCase(),
      expiry_month: cardInfo.expiry_month.padStart(2, '0'),
      expiry_year: year,
      cvv: cardInfo.cvv.replace(/\D/g, ''),
    };
  };
  
  const validateForm = () => {
    if (!guestInfo.first_name || !guestInfo.last_name) {
      toast.error('Lütfen ad ve soyad giriniz');
      return false;
    }
    if (!guestInfo.email || !guestInfo.email.includes('@')) {
      toast.error('Geçerli bir e-posta adresi giriniz');
      return false;
    }
    if (!guestInfo.phone || guestInfo.phone.length < 10) {
      toast.error('Geçerli bir telefon numarası giriniz');
      return false;
    }
    const cardDigits = cardInfo.card_number.replace(/\D/g, '');
    if (!cardDigits || cardDigits.length < 15 || cardDigits.length > 16) {
      toast.error('Geçerli bir kart numarası giriniz (15-16 hane)');
      return false;
    }
    if (!cardInfo.card_holder || cardInfo.card_holder.trim().length < 3) {
      toast.error('Kart üzerindeki ismi giriniz');
      return false;
    }
    if (!cardInfo.expiry_month || !cardInfo.expiry_year) {
      toast.error('Kart son kullanma tarihini seçiniz');
      return false;
    }
    if (!cardInfo.cvv || cardInfo.cvv.length < 3) {
      toast.error('CVV kodunu giriniz');
      return false;
    }
    if (!acceptTerms) {
      toast.error('Lütfen şartları kabul ediniz');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    setPaymentError(null);
    const cardPayload = buildZiraatCardPayload();
    
    try {
      const { hotel, offer, searchContext } = bookingData;
      
      // 1. Create booking
      const bookingResponse = await api.post('/hotel-booking/create', {
        hotel_id: hotel.id,
        hotel_name: hotel.name,
        pool_search_id: searchContext.poolSearchId,
        room: {
          offer_id: offer.offerId,
          room_name: offer.roomName,
          board_name: offer.boardName,
          price: offer.price.total,
          currency: 'EUR'
        },
        guest: guestInfo,
        check_in: searchContext.searchRequest.check_in,
        check_out: searchContext.searchRequest.check_out,
        nights: searchContext.nights,
        adults: searchContext.guests?.adults || 2,
        children: searchContext.guests?.children || 0,
        rooms: searchContext.guests?.rooms || 1,
        payment_method: 'ziraat',
        total_price: offer.price.total,
        original_price: offer.price.original,
        currency: 'EUR'
      });
      
      if (!bookingResponse.data.success) {
        throw new Error(bookingResponse.data.message || 'Rezervasyon oluşturulamadı');
      }
      
      const bookingId = bookingResponse.data.booking_id;
      const details = bookingResponse.data.booking_details || {};
      const totalEur = Number(details.total_eur ?? offer.price.total);
      const totalTry = Number(details.total_try);
      if (!totalTry || totalTry <= 0) {
        throw new Error('Ödeme tutarı hesaplanamadı');
      }

      // 2. Initiate Ziraat payment (TRY tahsilat; tutar işlem anı kuruna göre EUR fiyatından)
      sessionStorage.setItem('pendingHotelBookingId', bookingId);

      const paymentResponse = await api.post('/ziraat/initiate', {
        order_id: bookingId,
        ...cardPayload,
        amount: totalTry,
        currency: 'TRY',
        installment: 1,
        customer_email: guestInfo.email
      });
      
      if (paymentResponse.data.success && paymentResponse.data.html_form) {
        document.open();
        document.write(paymentResponse.data.html_form);
        document.close();
        return;
      } else {
        // Store booking info for success page
        sessionStorage.setItem('completedBooking', JSON.stringify({
          bookingId,
          hotel: hotel.name,
          checkIn: searchContext.searchRequest.check_in,
          checkOut: searchContext.searchRequest.check_out,
          room: offer.roomName,
          totalEur,
          totalTry,
          eurToTryRate: details.eur_to_try_rate
        }));
        
        // Navigate to success
        navigate(`/hotels/booking/success?id=${bookingId}`);
      }
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.message || 'Rezervasyon sırasında bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (!bookingData) {
    return null;
  }
  
  const { hotel, offer, searchContext } = bookingData;
  const guests = searchContext?.guests || { adults: 2, children: 0, rooms: 1, childAges: [] };
  const nights = searchContext?.nights || 1;
  const totalEur = Number(offer?.price?.total || 0);
  const perNightEur = nights > 0 ? totalEur / nights : totalEur;
  const totalGuests = (guests.adults || 0) + (guests.children || 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          {t('hotelBooking.back') || 'Geri Dön'}
        </Button>
        
        <h1 className="text-2xl font-bold mb-6">{t('hotelBooking.title') || 'Rezervasyon Tamamla'}</h1>

        {paymentError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Ödeme tamamlanamadı</AlertTitle>
            <AlertDescription>{paymentError}</AlertDescription>
          </Alert>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-6">
            {/* Guest Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t('hotelBooking.guestInfo') || 'Misafir Bilgileri'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('hotelBooking.firstName') || 'Ad'} *</Label>
                    <Input
                      value={guestInfo.first_name}
                      onChange={(e) => handleGuestChange('first_name', e.target.value)}
                      placeholder="John"
                      data-testid="guest-first-name"
                    />
                  </div>
                  <div>
                    <Label>{t('hotelBooking.lastName') || 'Soyad'} *</Label>
                    <Input
                      value={guestInfo.last_name}
                      onChange={(e) => handleGuestChange('last_name', e.target.value)}
                      placeholder="Doe"
                      data-testid="guest-last-name"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>{t('hotelBooking.email') || 'E-posta'} *</Label>
                  <Input
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => handleGuestChange('email', e.target.value)}
                    placeholder="john@example.com"
                    data-testid="guest-email"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('hotelBooking.phone') || 'Telefon'} *</Label>
                    <Input
                      value={guestInfo.phone}
                      onChange={(e) => handleGuestChange('phone', e.target.value)}
                      placeholder="+90 5XX XXX XX XX"
                      data-testid="guest-phone"
                    />
                  </div>
                  <div>
                    <Label>{t('hotelBooking.nationality') || 'Uyruk'}</Label>
                    <Select 
                      value={guestInfo.nationality} 
                      onValueChange={(v) => handleGuestChange('nationality', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TR">Türkiye</SelectItem>
                        <SelectItem value="DE">Almanya</SelectItem>
                        <SelectItem value="GB">İngiltere</SelectItem>
                        <SelectItem value="US">ABD</SelectItem>
                        <SelectItem value="FR">Fransa</SelectItem>
                        <SelectItem value="NL">Hollanda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Payment Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t('hotelBooking.paymentInfo') || 'Ödeme Bilgileri'}
                  <Badge variant="outline" className="ml-2">
                    <Lock className="w-3 h-3 mr-1" />
                    3D Secure
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="pb-4 border-b">
                  <PaymentBrandLogos
                    bankLabel={t('hotelBooking.ziraatPos') || 'Ziraat Bankası Sanal POS'}
                  />
                </div>
                
                <div>
                  <Label>{t('hotelBooking.cardNumber') || 'Kart Numarası'} *</Label>
                  <Input
                    value={cardInfo.card_number}
                    onChange={(e) => handleCardChange('card_number', e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    maxLength={19}
                    data-testid="card-number"
                  />
                </div>
                
                <div>
                  <Label>{t('hotelBooking.cardHolder') || 'Kart Üzerindeki İsim'} *</Label>
                  <Input
                    value={cardInfo.card_holder}
                    onChange={(e) => handleCardChange('card_holder', e.target.value.toUpperCase())}
                    placeholder="JOHN DOE"
                    data-testid="card-holder"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{t('hotelBooking.expiryMonth') || 'Ay'} *</Label>
                    <Select
                      value={cardInfo.expiry_month}
                      onValueChange={(v) => handleCardChange('expiry_month', v)}
                    >
                      <SelectTrigger data-testid="card-expiry-month">
                        <SelectValue placeholder="AA" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('hotelBooking.expiryYear') || 'Yıl'} *</Label>
                    <Select
                      value={cardInfo.expiry_year}
                      onValueChange={(v) => handleCardChange('expiry_year', v)}
                    >
                      <SelectTrigger data-testid="card-expiry-year">
                        <SelectValue placeholder="YY" />
                      </SelectTrigger>
                      <SelectContent>
                        {expiryYearOptions.map((y) => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>CVV *</Label>
                    <Input
                      type="password"
                      value={cardInfo.cvv}
                      onChange={(e) => handleCardChange('cvv', e.target.value.replace(/\D/g, ''))}
                      placeholder="***"
                      maxLength={4}
                      data-testid="card-cvv"
                    />
                  </div>
                </div>
                
                {/* Security notice */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-800 font-medium text-sm">
                      {t('hotelBooking.securePayment') || 'Güvenli Ödeme'}
                    </p>
                    <p className="text-green-600 text-xs">
                      {t('hotelBooking.securePaymentDesc') || 'Ödemeniz 3D Secure ile korunmaktadır. Kart bilgileriniz şifrelenerek iletilir.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Terms */}
            <Card>
              <CardContent className="pt-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1"
                    data-testid="accept-terms"
                  />
                  <span className="text-sm text-gray-600">
                    {t('hotelBooking.termsText') || 'Rezervasyon koşullarını, iptal politikasını ve gizlilik sözleşmesini okudum, kabul ediyorum.'}
                  </span>
                </label>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>{t('hotelBooking.summary') || 'Rezervasyon Özeti'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Hotel Info */}
                <div className="pb-4 border-b">
                  <h3 className="font-semibold">{hotel.name}</h3>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {searchContext?.hotel?.address?.city || 'Turkey'}
                  </p>
                </div>
                
                {/* Room Info */}
                <div className="pb-4 border-b">
                  <div className="flex items-center gap-2 mb-2">
                    <Coffee className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{offer.roomName}</span>
                  </div>
                  <Badge variant="secondary">{offer.boardName}</Badge>
                </div>
                
                {/* Guests */}
                <div className="pb-4 border-b space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                    <Users className="w-4 h-4 text-indigo-600" />
                    {t('hotelBooking.guestSummary') || 'Misafirler'}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('hotelBooking.adults') || 'Yetişkin'}</span>
                    <span className="font-medium">{guests.adults ?? 2}</span>
                  </div>
                  {(guests.children ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('hotelBooking.children') || 'Çocuk'}</span>
                      <span className="font-medium">{guests.children}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('hotelBooking.rooms') || 'Oda'}</span>
                    <span className="font-medium">{guests.rooms ?? 1}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-1 border-t border-dashed">
                    <span className="text-gray-600">{t('hotelBooking.totalGuests') || 'Toplam kişi'}</span>
                    <span className="font-semibold">{totalGuests}</span>
                  </div>
                </div>

                {/* Dates */}
                <div className="pb-4 border-b space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('hotelBooking.checkIn') || 'Giriş'}</span>
                    <span className="font-medium">
                      {format(new Date(searchContext.searchRequest.check_in), 'd MMMM yyyy', { locale: getDateLocale() })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('hotelBooking.checkOut') || 'Çıkış'}</span>
                    <span className="font-medium">
                      {format(new Date(searchContext.searchRequest.check_out), 'd MMMM yyyy', { locale: getDateLocale() })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('hotelBooking.nights') || 'Süre'}</span>
                    <span className="font-medium">{searchContext.nights} {t('hotelBooking.nightsText') || 'gece'}</span>
                  </div>
                </div>
                
                {/* Price */}
                <div className="space-y-2">
                  {nights > 1 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('hotelBooking.perNight') || 'Gecelik (ortalama)'}</span>
                      <span>€{perNightEur.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {t('hotelBooking.roomPrice') || 'Oda fiyatı'}
                      {nights > 1 ? ` (${nights} ${t('hotelBooking.nightsText') || 'gece'})` : ''}
                    </span>
                    <span>€{totalEur.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{t('hotelBooking.taxIncluded') || 'Vergiler dahil'}</span>
                    <span>{t('hotelBooking.currencyEur') || 'EUR'}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>{t('hotelBooking.total') || 'Toplam'}</span>
                      <span className="text-indigo-600">€{totalEur.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Submit Button */}
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 mt-4"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="complete-booking-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('hotelBooking.processing') || 'İşleniyor...'}
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      {t('hotelBooking.payAndBook') || 'Öde ve Rezervasyon Yap'}
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-center text-gray-500">
                  {t('hotelBooking.paymentNote') || 'Ödeme Ziraat Bankası 3D Secure ile yapılacaktır. Tahsilat, işlem anındaki güncel kur ile Euro tutarınıza göre gerçekleştirilir.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelBookingPage;
